import React, { useEffect, useMemo, useState } from 'react';
import i18n from './i18n';
import axios from 'axios';
import { API_URL } from './constants';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Image as ImageIcon, X } from '@phosphor-icons/react';

const formatRetailer = (retailer) =>
  retailer ? retailer.charAt(0).toUpperCase() + retailer.slice(1) : retailer;

const screenshotUrl = (retailer, country, date, filename) =>
  `${API_URL}/jti/banner-screenshots/${encodeURIComponent(retailer)}/${encodeURIComponent(country)}/${encodeURIComponent(date)}/${encodeURIComponent(filename)}`;

const t = i18n.getFixedT('en');

const BannerScreenshotsPanel = () => {
  const [combinations, setCombinations] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(true);
  const [indexError, setIndexError] = useState(null);
  const [retailer, setRetailer] = useState('');
  const [country, setCountry] = useState('');
  const [date, setDate] = useState('');
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [pagesError, setPagesError] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingIndex(true);
        setIndexError(null);
        const response = await axios.get(`${API_URL}/jti/banner-screenshots`);
        if (cancelled) return;
        const items = response.data?.combinations || [];
        setCombinations(items);
        if (items.length > 0) {
          const newest = items[0];
          setRetailer(newest.retailer);
          setCountry(newest.country);
          setDate(newest.date);
        }
      } catch (err) {
        if (!cancelled) setIndexError(t('bannerEvolution.screenshots.loadError'));
      } finally {
        if (!cancelled) setLoadingIndex(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const retailers = useMemo(
    () => [...new Set(combinations.map((item) => item.retailer))].sort(),
    [combinations]
  );

  const countries = useMemo(
    () =>
      [...new Set(combinations.filter((item) => item.retailer === retailer).map((item) => item.country))].sort(),
    [combinations, retailer]
  );

  const dates = useMemo(
    () =>
      combinations
        .filter((item) => item.retailer === retailer && item.country === country)
        .map((item) => item.date)
        .sort((a, b) => b.localeCompare(a)),
    [combinations, retailer, country]
  );

  useEffect(() => {
    if (!retailer || !countries.includes(country)) {
      if (countries[0] && countries[0] !== country) setCountry(countries[0]);
    }
  }, [retailer, countries, country]);

  useEffect(() => {
    if (!date || !dates.includes(date)) {
      if (dates[0] && dates[0] !== date) setDate(dates[0]);
    }
  }, [dates, date]);

  useEffect(() => {
    if (!retailer || !country || !date) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingPages(true);
        setPagesError(null);
        const response = await axios.get(
          `${API_URL}/jti/banner-screenshots/${encodeURIComponent(retailer)}/${encodeURIComponent(country)}/${encodeURIComponent(date)}`
        );
        if (cancelled) return;
        setPages(response.data?.pages || []);
      } catch (err) {
        if (!cancelled) {
          setPages([]);
          setPagesError(t('bannerEvolution.screenshots.loadError'));
        }
      } finally {
        if (!cancelled) setLoadingPages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [retailer, country, date, t]);

  if (loadingIndex) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
        <CircularProgress />
      </Box>
    );
  }

  if (indexError) {
    return <Alert severity="error">{indexError}</Alert>;
  }

  if (combinations.length === 0) {
    return <Alert severity="info">{t('bannerEvolution.screenshots.emptyIndex')}</Alert>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('bannerEvolution.screenshots.description')}
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('bannerEvolution.retailers')}</InputLabel>
              <Select
                value={retailer}
                label={t('bannerEvolution.retailers')}
                onChange={(event) => setRetailer(event.target.value)}
              >
                {retailers.map((item) => (
                  <MenuItem key={item} value={item}>{formatRetailer(item)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('bannerEvolution.country')}</InputLabel>
              <Select
                value={countries.includes(country) ? country : ''}
                label={t('bannerEvolution.country')}
                onChange={(event) => setCountry(event.target.value)}
              >
                {countries.map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('bannerEvolution.screenshots.date')}</InputLabel>
              <Select
                value={dates.includes(date) ? date : ''}
                label={t('bannerEvolution.screenshots.date')}
                onChange={(event) => setDate(event.target.value)}
              >
                {dates.map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t('bannerEvolution.screenshots.count', { count: pages.length })}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {pagesError && <Alert severity="error" sx={{ mb: 2 }}>{pagesError}</Alert>}

      {loadingPages ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
          <CircularProgress />
        </Box>
      ) : pages.length === 0 ? (
        <Alert severity="info">{t('bannerEvolution.screenshots.empty')}</Alert>
      ) : (
        <ImageList cols={3} gap={16}>
          {pages.map((page) => {
            const src = screenshotUrl(retailer, country, date, page.filename);
            return (
              <ImageListItem
                key={page.filename}
                sx={{ cursor: 'pointer', bgcolor: '#f5f5f5', borderRadius: 1, overflow: 'hidden' }}
                onClick={() => setPreview({ ...page, src })}
              >
                <img
                  src={src}
                  alt={page.label}
                  loading="lazy"
                  style={{ width: '100%', height: 220, objectFit: 'cover', objectPosition: 'top' }}
                />
                <ImageListItemBar
                  title={page.label}
                  actionIcon={<ImageIcon size={18} color="#fff" style={{ marginRight: 8 }} />}
                />
              </ImageListItem>
            );
          })}
        </ImageList>
      )}

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {preview?.label}
          <IconButton aria-label={t('bannerEvolution.close')} onClick={() => setPreview(null)}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {preview && (
            <img
              src={preview.src}
              alt={preview.label}
              style={{ display: 'block', width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BannerScreenshotsPanel;
